import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import api from '../services/api';
import { determineDisplayType, formatFieldName, generateId } from '../utils/fieldEditorUtils';
import { streamAutoConfigure } from '../utils/sseProgress';
import { getEditorSaveQueue } from '../utils/editorSaveQueue';

const MAX_HISTORY = 50;

function sameFieldName(left, right) {
  return (left || '').trim().toLowerCase() === (right || '').trim().toLowerCase();
}

function boundsDistance(left, right) {
  if (!left || !right) return Number.POSITIVE_INFINITY;
  return Math.abs(left.x - right.x)
    + Math.abs(left.y - right.y)
    + Math.abs(left.width - right.width)
    + Math.abs(left.height - right.height);
}

// PDF field names are not unique. Some forms contain a native AcroForm field and
// an AI field with the same label, so name-only lookups can update the hidden field
// while the visible field snaps back after reload. configId is the stable identity.
function findConfigIndexForField(fields, field) {
  if (!field || !Array.isArray(fields)) return -1;
  if (field.configId) {
    const byId = fields.findIndex(config => config.id === field.configId);
    if (byId >= 0) return byId;
  }

  const candidates = fields
    .map((config, index) => ({ config, index }))
    .filter(({ config }) => sameFieldName(config.pdfFieldName, field.name))
    .filter(({ config }) => !config.page || !field.page || config.page === field.page);
  if (candidates.length === 0) return -1;

  const visible = candidates.filter(({ config }) => config.isVisible !== false);
  const pool = visible.length > 0 ? visible : candidates;
  return pool
    .sort((a, b) => boundsDistance(a.config.bounds, field.bounds) - boundsDistance(b.config.bounds, field.bounds))[0]
    .index;
}

// The field clipboard is mirrored to localStorage so fields can be copied in one form
// (or one tab) and pasted into another. Keeping it purely in React state confined
// copy/paste to a single mounted editor, which is not how staff build a family of
// similar forms: they open the reference form in one tab and the new one in another.
//
// What is stored is already form-agnostic: paste regenerates the field name, id and
// formId and copies only geometry, type, group name and styling (see pasteFields), so
// nothing in the payload refers back to the source form.
const CLIPBOARD_STORAGE_KEY = 'pfp.fieldEditor.clipboard';

function readStoredClipboard() {
  try {
    const raw = window.localStorage.getItem(CLIPBOARD_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    // Reject anything that is not the shape pasteFields expects, so a stale or
    // hand-edited entry cannot throw inside the paste loop.
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(item => item && item.bounds
      && typeof item.bounds.x === 'number' && typeof item.bounds.y === 'number'
      && typeof item.bounds.width === 'number' && typeof item.bounds.height === 'number');
  } catch {
    return [];
  }
}

function writeStoredClipboard(items) {
  try {
    window.localStorage.setItem(CLIPBOARD_STORAGE_KEY, JSON.stringify(items));
  } catch {
    // Quota or private-mode failure: the in-memory clipboard still works within this
    // tab, so copy/paste degrades to its previous single-form behaviour rather than
    // breaking.
  }
}

export function useFieldEditor(formId) {
  // Core state
  const [configuration, renderConfiguration] = useState(null);
  const [pdfFields, renderPdfFields] = useState([]);
  // Event handlers can update several selected fields before React renders again.
  // Synchronous refs keep those edits cumulative without side effects in state updaters.
  const configurationRef = useRef(null);
  const pdfFieldsRef = useRef([]);
  const setConfiguration = useCallback((update) => {
    const next = typeof update === 'function' ? update(configurationRef.current) : update;
    configurationRef.current = next;
    renderConfiguration(next);
  }, []);
  const setPdfFields = useCallback((update) => {
    const next = typeof update === 'function' ? update(pdfFieldsRef.current) : update;
    pdfFieldsRef.current = next;
    renderPdfFields(next);
  }, []);
  const [selectedFieldIndex, setSelectedFieldIndex] = useState(null);
  const [selectedFieldIndices, setSelectedFieldIndices] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [aiProgress, setAiProgress] = useState({ show: false, progress: 0, layer: 0 });

  // Clipboard. Seeded from localStorage so a paste works immediately in a freshly
  // opened editor, and kept in sync with other tabs through the storage event.
  const [clipboard, setClipboard] = useState(readStoredClipboard);
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === CLIPBOARD_STORAGE_KEY) setClipboard(readStoredClipboard());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Undo/Redo
  const historyStackRef = useRef([]);
  const historyIndexRef = useRef(-1);

  // Custom field counter
  const customFieldCounterRef = useRef(0);

  // FIX: Guard against stale loadForm calls (StrictMode double-mount race condition)
  const loadFormCallIdRef = useRef(0);

  // SSE cleanup ref for auto-configure stream
  const sseCleanupRef = useRef(null);

  // ---- HISTORY ----
  const saveToHistory = useCallback((next = configurationRef.current) => {
    if (!next) return;
    const snapshot = JSON.parse(JSON.stringify(next));
    const stack = historyStackRef.current;
    const idx = historyIndexRef.current;
    if (JSON.stringify(stack[idx]) === JSON.stringify(snapshot)) return;
    // Remove future history
    if (idx < stack.length - 1) {
      historyStackRef.current = stack.slice(0, idx + 1);
    }
    historyStackRef.current.push(snapshot);
    if (historyStackRef.current.length > MAX_HISTORY) {
      historyStackRef.current.shift();
    } else {
      historyIndexRef.current++;
    }
  }, []);

  const clearHistory = useCallback(() => {
    historyStackRef.current = [];
    historyIndexRef.current = -1;
  }, []);

  // ---- SAVE CONFIG TO SERVER ----
  const saveQueue = useMemo(() => getEditorSaveQueue(formId,
    config => api.put(`/admin/forms/${encodeURIComponent(formId)}/configuration`, config)
  ), [formId]);
  const saveConfigToServer = useCallback(async (config) => {
    if (!formId || !config) return;
    await saveQueue.save(config);
  }, [formId, saveQueue]);

  // ---- LOAD FORM ----
  const loadForm = useCallback(async () => {
    if (!formId) return;

    // FIX: Increment call ID to detect stale calls (StrictMode double-mount race)
    loadFormCallIdRef.current++;
    const thisCallId = loadFormCallIdRef.current;
    console.log(`[loadForm] START callId=${thisCallId} formId=${formId}`);

    setLoading(true);
    try {
      // Reset/reload must not read the server while older local writes are in flight.
      await saveQueue.waitForIdle();
      // Load PDF fields
      const noCache = { headers: { 'Cache-Control': 'no-cache' }, params: { _: Date.now() } };
      const pdfData = (await api.get(`/admin/forms/${formId}/pdf-fields`, noCache)).data;
      let fields = pdfData.pdfFields || [];
      console.log(`[loadForm] pdfFields from server count=${fields.length}`, fields.map(f => f.name));

      // FIX: Check if this call has been superseded by a newer one
      if (thisCallId !== loadFormCallIdRef.current) {
        console.log(`[loadForm] STALE callId=${thisCallId}, current=${loadFormCallIdRef.current} — discarding after pdf-fields`);
        return;
      }

      // Try to load existing configuration
      let config;
      try {
        config = (await api.get(`/admin/forms/${formId}/configuration`, noCache)).data;
        console.log(
          `[loadForm] config from server fields=${config.fields?.length || 0}`,
          (config.fields || []).map(f => ({ name: f.pdfFieldName, visible: f.isVisible, custom: f.isCustomField }))
        );
      } catch {
          config = {
            formId: formId,
            formName: pdfData.formName,
            fields: [],
            markStyle: 'auto',
            radioStyle: 'dot',
            dateFormat: 'DD/MM/YYYY',
            conditionalFieldsEnabled: false
          };
      }

      // FIX: Check again after second API call
      if (thisCallId !== loadFormCallIdRef.current) {
        console.log(`[loadForm] STALE callId=${thisCallId}, current=${loadFormCallIdRef.current} — discarding after configuration`);
        return;
      }

      // Associate every extracted PDF widget with one saved configuration by ID.
      // Matching by name alone is unsafe because a form may contain two fields with
      // the same label at different positions.
      const representedConfigIds = new Set();
      if (config?.fields) {
        fields = fields.map(field => {
          const candidates = config.fields
            .filter(saved => !representedConfigIds.has(saved.id))
            .filter(saved => sameFieldName(saved.pdfFieldName, field.name))
            .filter(saved => !saved.page || !field.page || saved.page === field.page)
            .sort((a, b) => boundsDistance(a.bounds, field.bounds) - boundsDistance(b.bounds, field.bounds));
          const match = candidates[0];
          if (!match) return field;
          representedConfigIds.add(match.id);
          return {
            ...field,
            configId: match.id,
            bounds: match.bounds || field.bounds,
            page: match.page || field.page
          };
        }).filter(field => {
          if (!field.configId) return true;
          return config.fields.find(saved => saved.id === field.configId)?.isVisible !== false;
        });
      }

      // Restore fields from configuration that have bounds
      if (config && config.fields) {
        const fieldsWithBounds = config.fields.filter(f => f.bounds && f.isVisible !== false);
        customFieldCounterRef.current = 0;
        fieldsWithBounds.forEach(fieldConfig => {
          if (representedConfigIds.has(fieldConfig.id)) return;
          if (fieldConfig.isCustomField) {
            const match = fieldConfig.pdfFieldName.match(/custom_\w+_(\d+)/);
            if (match) {
              const num = parseInt(match[1]);
              if (num > customFieldCounterRef.current) {
                customFieldCounterRef.current = num;
              }
            }
          }
          fields.push({
            name: fieldConfig.pdfFieldName,
            type: fieldConfig.displayType,
            page: fieldConfig.page || 1,
            bounds: fieldConfig.bounds,
            configId: fieldConfig.id,
            label: fieldConfig.displayLabel
          });
          representedConfigIds.add(fieldConfig.id);
          console.log('[loadForm] restored field from config', fieldConfig.pdfFieldName);
        });
      }

      // Auto-populate configuration with PDF fields
      // Also sync pdfFields bounds with saved config bounds (so moved fields stay in place)
      let configChanged = false;
      if (fields && fields.length > 0) {
        fields.forEach((field, idx) => {
          const existingConfigIndex = findConfigIndexForField(config.fields, field);
          const existingConfig = existingConfigIndex >= 0 ? config.fields[existingConfigIndex] : null;
          if (existingConfig) {
            // If config has saved bounds, update the pdfField's bounds to match
            // This ensures moved fields stay at their saved position
            if (existingConfig.bounds) {
              fields[idx] = { ...field, configId: existingConfig.id, bounds: existingConfig.bounds };
            }
          } else {
            console.log('[loadForm] auto-adding missing config for field', field.name);
            const fieldId = generateId();
            config.fields.push({
              id: fieldId,
              formId: formId,
              pdfFieldName: field.name,
              displayType: determineDisplayType(field.type),
              groupName: field.groupName || null,
              displayLabel: null,
              minSize: null,
              maxSize: null,
              isVisible: true,
              displayOrder: config.fields.length,
              bounds: field.bounds,
              page: field.page,
              isCustomField: false,
              maxLength: field.maxLength || null
            });
            fields[idx] = { ...field, configId: fieldId };
            configChanged = true;
          }
        });
        if (configChanged) {
          console.log('[loadForm] configChanged=true auto-saving config', config.fields.map(f => ({
            name: f.pdfFieldName,
            visible: f.isVisible,
            custom: f.isCustomField
          })));
          // FIX: Check staleness before saving to server
          if (thisCallId !== loadFormCallIdRef.current) {
            console.log(`[loadForm] STALE callId=${thisCallId} — skipping auto-save`);
            return;
          }
          try {
            await saveConfigToServer(config);
          } catch (e) {
            console.error('Failed to auto-save configuration:', e);
          }
        }
      }

      // FIX: Final staleness check before setting state
      if (thisCallId !== loadFormCallIdRef.current) {
        console.log(`[loadForm] STALE callId=${thisCallId} — discarding before setState`);
        return;
      }

      console.log(`[loadForm] COMPLETE callId=${thisCallId} fields=${fields.length} configFields=${config.fields.length}`);
      setPdfFields(fields);
      setConfiguration(config);
      setSelectedFieldIndex(null);
      setSelectedFieldIndices(new Set());
      clearHistory();
      // Save initial state to history after a tick
      setTimeout(() => {
        historyStackRef.current = [JSON.parse(JSON.stringify(config))];
        historyIndexRef.current = 0;
      }, 0);
    } catch (error) {
      console.error('Error loading form:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [formId, saveConfigToServer, saveQueue, clearHistory, setPdfFields, setConfiguration]);

  // ---- SELECT FIELD ----
  const selectField = useCallback((index) => {
    setSelectedFieldIndices(new Set());
    setSelectedFieldIndex(index);
  }, []);

  const selectNextField = useCallback((direction = 1) => {
    if (pdfFields.length === 0) return null;

    const step = direction >= 0 ? 1 : -1;
    const currentIndex = selectedFieldIndex !== null
      ? selectedFieldIndex
      : (step > 0 ? -1 : pdfFields.length);
    const nextIndex = currentIndex + step;

    if (nextIndex < 0 || nextIndex >= pdfFields.length) {
      return null;
    }

    setSelectedFieldIndices(new Set());
    setSelectedFieldIndex(nextIndex);
    return nextIndex;
  }, [pdfFields, selectedFieldIndex]);

  // ---- TOGGLE FIELD SELECTION (multi-select) ----
  const toggleFieldSelection = useCallback((index) => {
    setSelectedFieldIndices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
    setSelectedFieldIndex(null);
  }, []);

  // ---- CLEAR SELECTION ----
  const clearSelection = useCallback(() => {
    setSelectedFieldIndices(new Set());
    setSelectedFieldIndex(null);
  }, []);

  // ---- SELECT ALL FIELDS ----
  const selectAllFields = useCallback((currentPage) => {
    const indices = new Set();
    pdfFields.forEach((field, index) => {
      if (!currentPage || field.page === currentPage) {
        indices.add(index);
      }
    });
    setSelectedFieldIndices(indices);
    setSelectedFieldIndex(null);
  }, [pdfFields]);

  const generateUniqueRadioGroupName = useCallback((baseName = 'radio_group') => {
    const existingNames = new Set(
      (configuration?.fields || [])
        .map(field => (field.groupName || '').trim())
        .filter(Boolean)
    );

    let candidate = baseName;
    let counter = 1;
    while (existingNames.has(candidate)) {
      counter += 1;
      candidate = `${baseName}_${counter}`;
    }
    return candidate;
  }, [configuration]);

  const assignSelectedFieldsToRadioGroup = useCallback(async (groupName) => {
    if (!configuration || selectedFieldIndices.size < 2) return false;

    const indices = Array.from(selectedFieldIndices);
    const selectedFields = indices.map(index => pdfFields[index]).filter(Boolean);
    if (selectedFields.length < 2) return false;

    const trimmedGroupName = (groupName || '').trim();
    if (!trimmedGroupName) return false;

    const newConfig = {
      ...configuration,
      fields: [...(configuration.fields || [])]
    };
    const configIdByField = new Map();

    selectedFields.forEach((field, orderIndex) => {
      const configIndex = findConfigIndexForField(newConfig.fields, field);
      const existingConfig = configIndex >= 0 ? newConfig.fields[configIndex] : null;
      const nextFieldConfig = {
        id: existingConfig ? existingConfig.id : generateId(),
        formId,
        pdfFieldName: field.name,
        displayType: 'radio',
        groupName: trimmedGroupName,
        displayLabel: existingConfig?.displayLabel || field.label || field.name,
        minSize: existingConfig?.minSize ?? null,
        maxSize: existingConfig?.maxSize ?? null,
        isVisible: existingConfig?.isVisible !== false,
        displayOrder: existingConfig?.displayOrder ?? orderIndex,
        isCustomField: existingConfig?.isCustomField || field.name.startsWith('custom_'),
        bounds: existingConfig?.bounds || field.bounds || null,
        page: existingConfig?.page || field.page || null,
        metadata: existingConfig?.metadata || null,
        fontSize: existingConfig?.fontSize ?? null,
        fontFamily: existingConfig?.fontFamily ?? null,
        underline: existingConfig?.underline ?? null,
        maxLength: existingConfig?.maxLength ?? null,
        bold: existingConfig?.bold ?? null,
        italic: existingConfig?.italic ?? null,
        required: existingConfig?.required ?? null,
        fontColor: existingConfig?.fontColor ?? null,
        textAlign: existingConfig?.textAlign ?? null
      };

      if (configIndex >= 0) {
        newConfig.fields[configIndex] = nextFieldConfig;
      } else {
        newConfig.fields.push(nextFieldConfig);
      }
      configIdByField.set(field, nextFieldConfig.id);
    });

    const updatedPdfFields = [...pdfFields];
    indices.forEach(index => {
      const field = updatedPdfFields[index];
      if (field) {
        updatedPdfFields[index] = {
          ...field,
          type: 'radio',
          groupName: trimmedGroupName,
          configId: configIdByField.get(field) || field.configId
        };
      }
    });

    setPdfFields(updatedPdfFields);
    setConfiguration(newConfig);
    await saveConfigToServer(newConfig);
    saveToHistory();
    return true;
  }, [configuration, selectedFieldIndices, pdfFields, formId, saveConfigToServer, saveToHistory]);

  // ---- SAVE FIELD CONFIGURATION ----
  const saveFieldConfiguration = useCallback(async (fieldData) => {
    const configuration = configurationRef.current;
    const pdfFields = pdfFieldsRef.current;
    if (selectedFieldIndex === null || !configuration) return;
    const field = pdfFields[selectedFieldIndex];
    if (!field) return;

    const configIndex = findConfigIndexForField(configuration.fields, field);
    const existingConfig = configIndex >= 0 ? configuration.fields[configIndex] : null;
    const isCustomField = (existingConfig && existingConfig.isCustomField) || field.name.startsWith('custom_');

    const fieldConfig = {
      id: existingConfig ? existingConfig.id : generateId(),
      formId: formId,
      pdfFieldName: fieldData.pdfFieldName, // Use the edited value from fieldData
      displayType: fieldData.displayType,
      groupName: fieldData.groupName || null,
      displayLabel: fieldData.displayLabel || null,
      minSize: fieldData.minSize ? parseInt(fieldData.minSize) : null,
      maxSize: fieldData.maxSize ? parseInt(fieldData.maxSize) : null,
      isVisible: fieldData.isVisible,
      displayOrder: parseInt(fieldData.displayOrder) || 0,
      isCustomField: isCustomField,
      bounds: existingConfig?.bounds || field.bounds || null,
      page: existingConfig?.page || field.page || null,
      metadata: existingConfig?.metadata || null,
      fontSize: fieldData.fontSize ? parseInt(fieldData.fontSize) : null,
      fontFamily: fieldData.fontFamily || null,
      underline: fieldData.underline || null,
      maxLength: fieldData.maxLength ? parseInt(fieldData.maxLength) : null,
      bold: fieldData.bold || null,
      italic: fieldData.italic || null,
      required: fieldData.required || null,
      fontColor: fieldData.fontColor && fieldData.fontColor !== '#000000' ? fieldData.fontColor : null,
      textAlign: fieldData.textAlign && fieldData.textAlign !== 'left' ? fieldData.textAlign : null,
      visibilityDependsOn: fieldData.visibilityDependsOn || null,
      visibilityDependsOnValue: fieldData.visibilityDependsOnValue || null
    };

    const newConfig = { ...configuration };
    const newFields = [...newConfig.fields];
    if (configIndex >= 0) {
      newFields[configIndex] = fieldConfig;
    } else {
      newFields.push(fieldConfig);
    }
    newConfig.fields = newFields;

    // Update the pdfFields array to reflect the new name
    const newPdfFields = [...pdfFields];
    newPdfFields[selectedFieldIndex] = { ...field, name: fieldData.pdfFieldName, configId: fieldConfig.id };
    setPdfFields(newPdfFields);

    setConfiguration(newConfig);
    saveToHistory();
    // Apply before awaiting; a late response must never restore its old UI snapshot.
    await saveConfigToServer(newConfig);
    return newConfig;
  }, [selectedFieldIndex, configuration, pdfFields, formId, saveConfigToServer, saveToHistory]);

  // ---- UPDATE FIELD BOUNDS (optimistic, supports rapid sequential calls) ----
  const updateFieldBounds = useCallback((fieldIndex, newBounds, newPage) => {
    const field = pdfFieldsRef.current[fieldIndex];
    const config = configurationRef.current;
    if (!field || !config) return;
    const bounds = { ...field.bounds, ...newBounds };
    const page = newPage || field.page || 1;
    const fields = [...config.fields];
    const index = findConfigIndexForField(fields, field);
    let configId = field.configId;
    if (index >= 0) {
      fields[index] = { ...fields[index], bounds, page };
      configId = fields[index].id;
    } else {
      configId = generateId();
      fields.push({
        id: configId, formId, pdfFieldName: field.name,
        displayType: determineDisplayType(field.type), groupName: null, displayLabel: null,
        minSize: null, maxSize: null, isVisible: true, displayOrder: 0,
        bounds, page, isCustomField: field.name.startsWith('custom_'), metadata: null
      });
    }
    const newConfig = { ...config, fields };
    setPdfFields(pdfFieldsRef.current.map((f, i) => i === fieldIndex ? { ...f, bounds, page, configId } : f));
    setConfiguration(newConfig);
    saveToHistory();
    saveConfigToServer(newConfig).catch(err => console.error('Failed to save:', err));
  }, [formId, saveConfigToServer, saveToHistory, setPdfFields, setConfiguration]);

  // Toolbar edits carry identity + a property patch, never an old whole-field snapshot.
  const saveFieldProperties = useCallback(async (fieldId, changes) => {
    const config = configurationRef.current;
    const index = config?.fields.findIndex(f => f.id === fieldId);
    if (index == null || index < 0 || config.fields[index].isVisible === false) return;
    const fields = [...config.fields];
    fields[index] = { ...fields[index], ...changes };
    const next = { ...config, fields };
    setConfiguration(next);
    saveToHistory();
    await saveConfigToServer(next);
  }, [setConfiguration, saveConfigToServer, saveToHistory]);

  const nudgeSelectedFields = useCallback((deltaX, deltaY) => {
    if (!configuration) return 0;

    const indicesToMove = selectedFieldIndices.size > 0
      ? Array.from(selectedFieldIndices)
      : (selectedFieldIndex !== null ? [selectedFieldIndex] : []);

    if (indicesToMove.length === 0) return 0;

    const movableFields = indicesToMove
      .map(index => ({ index, field: pdfFields[index] }))
      .filter(({ field }) => field?.bounds);

    if (movableFields.length === 0) return 0;

    saveToHistory();

    movableFields.forEach(({ index, field }) => {
      updateFieldBounds(index, {
        ...field.bounds,
        x: (field.bounds.x || 0) + deltaX,
        y: (field.bounds.y || 0) + deltaY
      });
    });

    return movableFields.length;
  }, [configuration, selectedFieldIndices, selectedFieldIndex, pdfFields, saveToHistory, updateFieldBounds]);

  // ---- DELETE FIELD (optimistic) ----
  const deleteField = useCallback(async (fieldIndex) => {
    if (!configuration) return;
    const field = pdfFields[fieldIndex];
    if (!field) return;
    console.log('[deleteField] requested delete', {
      fieldIndex,
      fieldName: field.name,
      fieldType: field.type
    });

    const newConfig = { ...configuration };
    const newFields = [...newConfig.fields];
    let configIdx = findConfigIndexForField(newFields, field);
    const fieldConfig = configIdx >= 0 ? newFields[configIdx] : null;
    const isCustomField = (fieldConfig && fieldConfig.isCustomField) || field.name.startsWith('custom_');

    if (isCustomField) {
      for (let i = 0; i < newFields.length; i++) {
        if ((newFields[i].pdfFieldName || '') === (field.name || '') && (newFields[i].isCustomField || field.name.startsWith('custom_'))) {
          newFields[i] = { ...newFields[i], isVisible: false };
        }
      }
    } else {
      if (fieldConfig) {
        newFields[configIdx] = { ...fieldConfig, isVisible: false };
      } else {
        newFields.push({
          id: generateId(),
          formId: formId,
          pdfFieldName: field.name,
          displayType: field.type || 'text',
          groupName: null,
          displayLabel: null,
          minSize: null,
          maxSize: null,
          isVisible: false,
          displayOrder: newFields.length,
          bounds: field.bounds,
          isCustomField: false
        });
      }
    }
    newConfig.fields = newFields;
    console.log('[deleteField] config after marking hidden', newConfig.fields.map(f => ({
      name: f.pdfFieldName,
      visible: f.isVisible,
      custom: f.isCustomField
    })));

    // Remove from pdfFields
    const newPdfFields = pdfFields.filter((_, i) => i !== fieldIndex);

    // Optimistic: update state first, then save in background
    setPdfFields(newPdfFields);
    setConfiguration(newConfig);
    setSelectedFieldIndex(null);
    setSelectedFieldIndices(new Set());
    saveToHistory();
    saveConfigToServer(newConfig).catch(err => console.error('Failed to save:', err));
  }, [configuration, pdfFields, formId, saveConfigToServer, saveToHistory]);

  // ---- DELETE FIELD BY NAME (optimistic) ----
  const deleteFieldByName = useCallback(async (fieldName, fieldId = null) => {
    if (!configuration) return;
    const newConfig = { ...configuration };
    const newFields = [...newConfig.fields];
    const fieldConfig = fieldId
      ? newFields.find(f => f.id === fieldId)
      : [...newFields].reverse().find(f => f.pdfFieldName === fieldName && f.isVisible !== false);
    if (!fieldConfig) return;

    const isCustomField = fieldConfig.isCustomField || fieldName.startsWith('custom_');

    if (isCustomField) {
      for (let i = 0; i < newFields.length; i++) {
        if ((newFields[i].pdfFieldName || '') === (fieldName || '') && (newFields[i].isCustomField || fieldName.startsWith('custom_'))) {
          newFields[i] = { ...newFields[i], isVisible: false };
        }
      }
    } else {
      const idx = newFields.findIndex(f => f.id === fieldConfig.id);
      if (idx >= 0) {
        newFields[idx] = { ...newFields[idx], isVisible: false };
      }
    }
    newConfig.fields = newFields;

    const newPdfFields = pdfFields.filter(f => fieldId ? f.configId !== fieldId : f.name !== fieldName);

    // Optimistic: update state first, then save in background
    setPdfFields(newPdfFields);
    setConfiguration(newConfig);
    setSelectedFieldIndex(null);
    saveToHistory();
    saveConfigToServer(newConfig).catch(err => console.error('Failed to save:', err));
  }, [configuration, pdfFields, saveConfigToServer, saveToHistory]);

  // ---- DELETE SELECTED FIELDS (optimistic) ----
  const deleteSelectedFields = useCallback(async () => {
    if (selectedFieldIndices.size === 0 || !configuration) return;

    const indicesToDelete = Array.from(selectedFieldIndices).sort((a, b) => b - a);
    const newConfig = { ...configuration };
    const newFields = [...newConfig.fields];
    let newPdfFields = [...pdfFields];

    indicesToDelete.forEach(index => {
      const field = newPdfFields[index];
      if (!field) return;
      const configIndex = findConfigIndexForField(newFields, field);
      const fieldConfig = configIndex >= 0 ? newFields[configIndex] : null;
      const isCustomField = fieldConfig && fieldConfig.isCustomField;

      if (isCustomField) {
        for (let i = 0; i < newFields.length; i++) {
          if ((newFields[i].pdfFieldName || '') === (field.name || '') && (newFields[i].isCustomField || field.name.startsWith('custom_'))) {
            newFields[i] = { ...newFields[i], isVisible: false };
          }
        }
      } else {
        if (fieldConfig) {
          newFields[configIndex] = { ...fieldConfig, isVisible: false };
        } else {
          newFields.push({
            id: generateId(),
            formId: formId,
            pdfFieldName: field.name,
            displayType: field.type || 'text',
            groupName: null,
            displayLabel: null,
            minSize: null,
            maxSize: null,
            isVisible: false,
            displayOrder: newFields.length,
            bounds: field.bounds,
            isCustomField: false
          });
        }
      }
      newPdfFields.splice(index, 1);
    });

    newConfig.fields = newFields;

    // Optimistic: update state first, then save in background
    setPdfFields(newPdfFields);
    setConfiguration(newConfig);
    setSelectedFieldIndices(new Set());
    setSelectedFieldIndex(null);
    saveToHistory();
    saveConfigToServer(newConfig).catch(err => console.error('Failed to save:', err));
  }, [selectedFieldIndices, configuration, pdfFields, formId, saveConfigToServer, saveToHistory]);

  // ---- CREATE CUSTOM FIELD (optimistic) ----
  const createCustomField = useCallback(async (x, y, width, height, fieldType, currentPage, pdfDocRef, scale) => {
    if (!pdfDocRef || !configuration) return;

    const page = await pdfDocRef.getPage(currentPage);
    const viewport = page.getViewport({ scale });
    const [pdfX1, pdfY1] = viewport.convertToPdfPoint(x, y + height);
    const [pdfX2, pdfY2] = viewport.convertToPdfPoint(x + width, y);
    const pdfX = Math.min(pdfX1, pdfX2);
    const pdfY = Math.min(pdfY1, pdfY2);
    const pdfWidth = Math.abs(pdfX2 - pdfX1);
    const pdfHeight = Math.abs(pdfY2 - pdfY1);
    const latest = configurationRef.current;
    if (!latest || latest.formId !== formId) return;

    customFieldCounterRef.current++;
    const fieldName = `custom_${fieldType}_${customFieldCounterRef.current}`;
    const fieldId = generateId();

    const customField = {
      name: fieldName,
      type: fieldType,
      page: currentPage,
      bounds: { x: pdfX, y: pdfY, width: pdfWidth, height: pdfHeight },
      configId: fieldId
    };

    const fieldConfig = {
      id: fieldId,
      formId: formId,
      pdfFieldName: fieldName,
      displayType: fieldType,
      groupName: null,
      displayLabel: null,
      minSize: null,
      maxSize: null,
      isVisible: true,
      displayOrder: latest.fields.length,
      bounds: customField.bounds,
      page: currentPage,
      isCustomField: true
    };

    const newPdfFields = [...pdfFieldsRef.current, customField];
    const newConfig = { ...latest, fields: [...latest.fields, fieldConfig] };

    // Optimistic: update state first, then save in background
    setPdfFields(newPdfFields);
    setConfiguration(newConfig);
    saveToHistory();
    saveConfigToServer(newConfig).catch(err => console.error('Failed to save:', err));
    return customField;
  }, [configuration, pdfFields, formId, saveConfigToServer, saveToHistory]);

  // ---- COPY SELECTED FIELDS ----
  const copySelectedFields = useCallback(() => {
    const indicesToCopy = selectedFieldIndices.size > 0
      ? Array.from(selectedFieldIndices)
      : (selectedFieldIndex !== null ? [selectedFieldIndex] : []);

    if (indicesToCopy.length === 0) return 0;

    const copied = [];
    indicesToCopy.forEach(index => {
      const field = pdfFields[index];
      if (field && field.bounds) {
        const configIndex = findConfigIndexForField(configuration?.fields, field);
        const fieldConfig = configIndex >= 0 ? configuration.fields[configIndex] : null;
        copied.push({
          type: field.type || (fieldConfig ? fieldConfig.displayType : 'text'),
          bounds: { ...field.bounds },
          page: field.page,
          config: fieldConfig ? { ...fieldConfig } : null
        });
      }
    });

    setClipboard(copied);
    writeStoredClipboard(copied);
    return copied.length;
  }, [selectedFieldIndices, selectedFieldIndex, pdfFields, configuration]);

  // ---- PASTE FIELDS ----
  const pasteFields = useCallback(async (mouseX, mouseY, currentPage, pdfDocRef, scale) => {
    if (clipboard.length === 0 || !pdfDocRef || !configurationRef.current) return 0;

    const page = await pdfDocRef.getPage(currentPage);
    const viewport = page.getViewport({ scale });
    const [pdfMouseX, pdfMouseY] = viewport.convertToPdfPoint(mouseX, mouseY);
    const configuration = configurationRef.current;
    const pdfFields = pdfFieldsRef.current;
    if (!configuration || configuration.formId !== formId) return 0;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    clipboard.forEach(item => {
      minX = Math.min(minX, item.bounds.x);
      minY = Math.min(minY, item.bounds.y);
      maxX = Math.max(maxX, item.bounds.x + item.bounds.width);
      maxY = Math.max(maxY, item.bounds.y + item.bounds.height);
    });
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    const offsetX = pdfMouseX - centerX;
    const offsetY = pdfMouseY - centerY;

    const newPdfFields = [...pdfFields];
    const newConfig = { ...configuration, fields: [...configuration.fields] };
    const newFieldIndices = [];

    // Radio group names are remapped, not copied verbatim. A group name only has to be
    // unique within one form, so the same name ("radio_group") very often exists in both
    // the source and the destination. Pasting it as-is would silently merge the pasted
    // radios into an unrelated existing group, and the patient could then pick only one
    // option across what should be two questions. Each distinct source group becomes
    // one fresh group in the destination, so grouping WITHIN the pasted set is kept.
    const takenGroupNames = new Set(
      configuration.fields.map(f => (f.groupName || '').trim()).filter(Boolean)
    );
    const groupNameMap = new Map();
    const remapGroupName = (source) => {
      const name = (source || '').trim();
      if (!name) return null;
      if (groupNameMap.has(name)) return groupNameMap.get(name);
      // Keep the source name when it is free; otherwise suffix until it is.
      let candidate = name;
      let n = 1;
      while (takenGroupNames.has(candidate)) {
        n += 1;
        candidate = `${name}_${n}`;
      }
      takenGroupNames.add(candidate);
      groupNameMap.set(name, candidate);
      return candidate;
    };

    for (const item of clipboard) {
      const newBounds = {
        x: item.bounds.x + offsetX,
        y: item.bounds.y + offsetY,
        width: item.bounds.width,
        height: item.bounds.height
      };
      const fieldType = item.type || 'text';
      customFieldCounterRef.current++;
      const newFieldName = `custom_${fieldType}_${customFieldCounterRef.current}`;
      const groupName = remapGroupName(item.config?.groupName);
      const newFieldId = generateId();

      const newField = {
        name: newFieldName,
        type: fieldType,
        page: currentPage,
        bounds: newBounds,
        groupName,
        configId: newFieldId
      };
      newPdfFields.push(newField);
      newFieldIndices.push(newPdfFields.length - 1);

      newConfig.fields.push({
        id: newFieldId,
        formId: formId,
        pdfFieldName: newFieldName,
        displayType: fieldType,
        groupName,
        displayLabel: null,
        minSize: item.config?.minSize || null,
        maxSize: item.config?.maxSize || null,
        isVisible: true,
        displayOrder: newConfig.fields.length,
        bounds: newBounds,
        page: currentPage,
        isCustomField: true,
        metadata: null,
        // Copy font styling properties from the original field
        fontFamily: item.config?.fontFamily || null,
        fontSize: item.config?.fontSize || null,
        fontColor: item.config?.fontColor || null,
        bold: item.config?.bold || false,
        italic: item.config?.italic || false,
        underline: item.config?.underline || false,
        required: item.config?.required || false,
        maxLength: item.config?.maxLength || null,
        textAlign: item.config?.textAlign || null
      });
    }

    setPdfFields(newPdfFields);
    setConfiguration(newConfig);
    // Select the pasted fields in the same batch as the field update. Selecting after
    // the save resolved left a render where pdfFields had changed but the selection
    // still pointed at the copied source field, and the canvas scrolled back to it.
    setSelectedFieldIndices(new Set(newFieldIndices));
    setSelectedFieldIndex(null);
    saveToHistory();
    await saveConfigToServer(newConfig);

    return newFieldIndices.length;
  }, [clipboard, pdfFields, configuration, formId, saveConfigToServer, saveToHistory]);

  // Canvas rendering and nudging use different representations. Restore both together,
  // including added/deleted fields, so the next edit starts from the displayed bounds.
  const restoreSnapshot = useCallback((snapshot) => {
    const previousFields = pdfFieldsRef.current;
    setConfiguration(snapshot);
    setPdfFields((snapshot?.fields || []).filter(f => f.isVisible !== false && f.bounds).map(f => ({
      ...previousFields.find(p => p.name === f.pdfFieldName),
      name: f.pdfFieldName, type: f.displayType, bounds: { ...f.bounds },
      page: f.page || 1, configId: f.id, groupName: f.groupName, label: f.displayLabel,
    })));
  }, [setConfiguration, setPdfFields]);

  // ---- UNDO ----
  const undo = useCallback(async () => {
    if (historyIndexRef.current <= 0) return false;
    historyIndexRef.current--;
    const snapshot = historyStackRef.current[historyIndexRef.current];
    const restored = JSON.parse(JSON.stringify(snapshot));

    const previous = configurationRef.current;
    const index = historyIndexRef.current;
    restoreSnapshot(restored);
    try {
      await saveConfigToServer(restored);
      if (configurationRef.current === restored) clearSelection();
      return true;
    } catch {
      if (configurationRef.current === restored && historyIndexRef.current === index) {
        restoreSnapshot(previous);
        historyIndexRef.current++;
      }
      return false;
    }
  }, [saveConfigToServer, clearSelection, restoreSnapshot]);

  // ---- REDO ----
  const redo = useCallback(async () => {
    if (historyIndexRef.current >= historyStackRef.current.length - 1) return false;
    historyIndexRef.current++;
    const snapshot = historyStackRef.current[historyIndexRef.current];
    const restored = JSON.parse(JSON.stringify(snapshot));

    const previous = configurationRef.current;
    const index = historyIndexRef.current;
    restoreSnapshot(restored);
    try {
      await saveConfigToServer(restored);
      if (configurationRef.current === restored) clearSelection();
      return true;
    } catch {
      if (configurationRef.current === restored && historyIndexRef.current === index) {
        restoreSnapshot(previous);
        historyIndexRef.current--;
      }
      return false;
    }
  }, [saveConfigToServer, clearSelection, restoreSnapshot]);

  // ---- AUTO-CONFIGURE ----
  const autoConfigureForm = useCallback((options = {}) => {
    if (!formId) return Promise.resolve();

    setAiProgress({ show: true, progress: 0, layer: -1 });

    return new Promise((resolve, reject) => {
      const layerProgressMap = { 0: 10, 1: 40, 2: 65, 3: 90 };

      const cleanup = streamAutoConfigure(
        formId,
        null,
        // onProgress
        (evt) => {
          const baseProgress = layerProgressMap[evt.layer] || 0;
          const progress = evt.status === 'completed'
            ? baseProgress
            : Math.max(baseProgress - 10, 0);
          setAiProgress({
            show: true,
            progress,
            layer: evt.layer,
            layerName: evt.name,
            layerStatus: evt.status,
            fieldsFound: evt.fieldsFound,
            error: evt.error
          });
        },
        // onComplete
        (result) => {
          setAiProgress({ show: false, progress: 100, layer: 4 });
          setConfiguration(result);
          resolve(result);
        },
        // onError
        (error) => {
          setAiProgress({ show: false, progress: 0, layer: 0 });
          reject(error);
        },
        null,
        Boolean(options.replaceExisting)
      );

      // Store cleanup in case component unmounts
      sseCleanupRef.current = cleanup;
    });
  }, [formId]);

  // ---- RE-ANALYZE ----
  const reAnalyzeFields = useCallback(async () => {
    if (!formId) return;
    setLoading(true);
    setAiProgress({ show: true, progress: 0, layer: 0 });
    try {
      const layers = [
        { duration: 500, progress: 10 },
        { duration: 3000, progress: 40 },
        { duration: 1500, progress: 65 },
        { duration: 4000, progress: 100 }
      ];
      const progressPromise = (async () => {
        for (let i = 0; i < layers.length; i++) {
          setAiProgress({ show: true, progress: layers[i].progress, layer: i });
          await new Promise(r => setTimeout(r, layers[i].duration));
        }
      })();

      const result = (await api.post(`/admin/forms/${formId}/re-analyze`)).data;
      await progressPromise;
      setAiProgress({ show: false, progress: 100, layer: 3 });
      return result;
    } catch (error) {
      setAiProgress({ show: false, progress: 0, layer: 0 });
      throw error;
    } finally {
      setLoading(false);
    }
  }, [formId]);

  // ---- SAVE ALL CONFIGURATION ----
  const saveAllConfiguration = useCallback(async (formLevelData) => {
    if (!configurationRef.current) return;
    const newConfig = { ...configurationRef.current, ...formLevelData };
    setConfiguration(newConfig);
    saveToHistory();
    await saveConfigToServer(newConfig);
    await saveQueue.flush();
  }, [setConfiguration, saveConfigToServer, saveQueue, saveToHistory]);

  // ---- TOGGLE FORM TYPE ----
  const toggleFormType = useCallback(async () => {
    if (!formId) return;
    const formInfo = (await api.get(`/forms/${formId}`)).data;
    const newIsInternal = !formInfo.isInternal;
    await api.put(`/admin/forms/${formId}/type`, { isInternal: newIsInternal });
    return newIsInternal ? 'Internal' : 'External';
  }, [formId]);

  // ---- RENAME FORM ----
  const renameForm = useCallback(async (newName) => {
    if (!formId) return;
    await api.put(`/admin/forms/${formId}/rename`, { newName });
  }, [formId]);

  // ---- GENERATE LINK ----
  const generateLink = useCallback(async (linkData) => {
    const response = await api.post('/formlinks', {
      formId: formId,
      ...linkData
    });
    return response.data;
  }, [formId]);

  // ---- DELETE TEMPLATE ----
  const deleteTemplate = useCallback(async () => {
    if (!formId) return;
    await api.delete(`/admin/forms/${formId}`);
  }, [formId]);

  // ---- DUPLICATE FIELD ----
  const duplicateField = useCallback(async (fieldConfig) => {
    if (!configuration || !fieldConfig) return;

    const sourceField = pdfFields.find(pf => pf.name === fieldConfig.pdfFieldName);
    if (!sourceField) return;

    customFieldCounterRef.current++;
    const fieldType = fieldConfig.displayType || sourceField.type || 'text';
    const newFieldName = `copy_${fieldConfig.pdfFieldName}_${customFieldCounterRef.current}`;

    // Offset the duplicate slightly so it's visible
    const newBounds = {
      x: (sourceField.bounds?.x || 0) + 15,
      y: (sourceField.bounds?.y || 0) - 15,
      width: sourceField.bounds?.width || 100,
      height: sourceField.bounds?.height || 20
    };

    const newPdfField = {
      name: newFieldName,
      type: fieldType,
      page: sourceField.page || 1,
      bounds: newBounds
    };

    const newConfigField = {
      id: generateId(),
      formId: formId,
      pdfFieldName: newFieldName,
      displayType: fieldType,
      groupName: fieldConfig.groupName || null,
      displayLabel: fieldConfig.displayLabel ? `${fieldConfig.displayLabel} (copy)` : null,
      minSize: fieldConfig.minSize || null,
      maxSize: fieldConfig.maxSize || null,
      isVisible: true,
      displayOrder: configuration.fields.length,
      bounds: newBounds,
      page: sourceField.page || 1,
      isCustomField: true,
      metadata: fieldConfig.metadata || null,
      // Copy font styling properties from the original field
      fontFamily: fieldConfig.fontFamily || null,
      fontSize: fieldConfig.fontSize || null,
      fontColor: fieldConfig.fontColor || null,
      bold: fieldConfig.bold || false,
      italic: fieldConfig.italic || false,
      underline: fieldConfig.underline || false,
      required: fieldConfig.required || false,
      maxLength: fieldConfig.maxLength || null,
      textAlign: fieldConfig.textAlign || null
    };

    const newPdfFields = [...pdfFields, newPdfField];
    const newConfig = {
      ...configuration,
      fields: [...configuration.fields, newConfigField]
    };

    saveToHistory(newConfig);
    setPdfFields(newPdfFields);
    setConfiguration(newConfig);
    await saveConfigToServer(newConfig);

    // Select the new field
    setSelectedFieldIndex(newPdfFields.length - 1);
    setSelectedFieldIndices(new Set([newPdfFields.length - 1]));
  }, [configuration, pdfFields, formId, saveConfigToServer, saveToHistory]);

  return {
    // State
    configuration,
    setConfiguration,
    pdfFields,
    setPdfFields,
    selectedFieldIndex,
    setSelectedFieldIndex,
    selectedFieldIndices,
    setSelectedFieldIndices,
    loading,
    aiProgress,
    clipboard,

    // Actions
    loadForm,
    selectField,
    selectNextField,
    toggleFieldSelection,
    clearSelection,
    selectAllFields,
    generateUniqueRadioGroupName,
    assignSelectedFieldsToRadioGroup,
    saveFieldConfiguration,
    saveFieldProperties,
    updateFieldBounds,
    nudgeSelectedFields,
    deleteField,
    deleteFieldByName,
    deleteSelectedFields,
    createCustomField,
    duplicateField,
    copySelectedFields,
    pasteFields,
    undo,
    redo,
    saveToHistory,
    autoConfigureForm,
    reAnalyzeFields,
    saveAllConfiguration,
    toggleFormType,
    renameForm,
    generateLink,
    deleteTemplate,
    saveConfigToServer
  };
}

export default useFieldEditor;
